import { useMemo } from 'react';
import { Layout, Menu, Breadcrumb, Avatar, Dropdown, Button, theme } from 'antd';
import type { MenuProps } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  ReadOutlined,
  FileTextOutlined,
  TeamOutlined,
  BellOutlined,
  LogoutOutlined,
  KeyOutlined,
  ExperimentOutlined,
  GiftOutlined,
  BugOutlined,
  DatabaseOutlined,
  AlignLeftOutlined,
  CreditCardOutlined,
  TagOutlined,
  MessageOutlined,
  BarChartOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { useUiStore } from '../store/ui';

const { Header, Sider, Content } = Layout;

const NAME_MAP: Record<string, string> = {
  dashboard: '统计',
  apps: '程序管理',
  logs: '日志查询',
  tutorial: '使用教程',
  profile: '个人中心',
  admin: '管理后台',
  users: '用户管理',
  agents: '代理管理',
  api: 'WebApi接口',
  variables: '变量数据',
  algorithms: '远程算法',
  blacklist: '黑名单列表',
  versions: '版本管理',
  keys: '单码管理',
  generate: '生成卡密',
  query: '查询',
  trial: '试用管理',
  devices: '设备列表',
  online: '在线设备',
  'error-codes': '错误代码对照表',
};

export default function MainLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const siderCollapsed = useUiStore((s) => s.siderCollapsed);
  const toggleSider = useUiStore((s) => s.toggleSider);
  const { token: antdToken } = theme.useToken();

  const menuItems = useMemo<NonNullable<MenuProps['items']>>(() => {
    const items: NonNullable<MenuProps['items']> = [
      { key: '/dashboard', icon: <BarChartOutlined />, label: '统计' },
      { key: '/profile', icon: <UserOutlined />, label: '个人中心' },
      { key: '/tutorial', icon: <ReadOutlined />, label: '使用教程' },
      {
        key: '/apps',
        icon: <LockOutlined />,
        label: '程序管理',
        children: [
          { key: '/apps', icon: <LockOutlined />, label: '软件列表' },
          { key: '/apps/1/api', icon: <AlignLeftOutlined />, label: 'WebApi接口' },
          { key: '/apps/1/variables', icon: <DatabaseOutlined />, label: '变量数据' },
          { key: '/apps/1/algorithms', icon: <AlignLeftOutlined />, label: '远程算法' },
          { key: '/apps/1/blacklist', icon: <BugOutlined />, label: '黑名单列表' },
        ],
      },
      { key: '/error-codes', icon: <BugOutlined />, label: '错误代码对照表' },
      {
        key: '/admin/users',
        icon: <TeamOutlined />,
        label: '用户管理',
        children: [
          { key: '/admin/users', icon: <TeamOutlined />, label: '用户列表' },
          { key: '/admin/users/cards', icon: <CreditCardOutlined />, label: '用户充值卡' },
        ],
      },
      {
        key: '/apps/1/keys',
        icon: <KeyOutlined />,
        label: '单码管理',
        children: [
          { key: '/apps/1/keys', icon: <KeyOutlined />, label: '单码列表' },
          {
            key: '/apps/1/keys/query',
            icon: <FileTextOutlined />,
            label: '激活/制卡查询',
            children: [
              { key: '/apps/1/keys/query', icon: <FileTextOutlined />, label: '查询' },
            ],
          },
        ],
      },
      {
        key: '/apps/1/trial',
        icon: <ExperimentOutlined />,
        label: '试用管理',
        children: [
          { key: '/apps/1/trial', icon: <ExperimentOutlined />, label: '试用列表' },
        ],
      },
      {
        key: '/admin/agents',
        icon: <GiftOutlined />,
        label: '代理管理',
        children: [
          { key: '/admin/agents', icon: <GiftOutlined />, label: '代理列表' },
          { key: '/admin/agents/cards', icon: <CreditCardOutlined />, label: '代理充值卡' },
          { key: '/admin/agents/types', icon: <TagOutlined />, label: '代理售卡类型' },
          { key: '/admin/agents/announcement', icon: <MessageOutlined />, label: '设置代理端公告' },
        ],
      },
      { key: '/logs', icon: <FileTextOutlined />, label: '日志查询' },
    ];
    return items;
  }, [user]);

  const breadcrumbItems = useMemo(() => {
    const snippets = location.pathname.split('/').filter(Boolean);
    const items = [{ title: '首页' }];
    snippets.forEach((s) => {
      items.push({ title: NAME_MAP[s] ?? s });
    });
    return items;
  }, [location.pathname]);

  const selectedKey = useMemo(() => {
    return location.pathname;
  }, [location.pathname]);

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    navigate(key);
  };

  const handleUserMenuClick: MenuProps['onClick'] = async ({ key }) => {
    if (key === 'logout') {
      await logout();
      navigate('/login', { replace: true });
    } else {
      navigate(key);
    }
  };

  const userMenuItems: MenuProps['items'] = [
    { key: '/profile', icon: <UserOutlined />, label: '个人中心' },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={siderCollapsed}
        theme="light"
        style={{ boxShadow: '2px 0 8px rgba(0,0,0,0.06)' }}
      >
        <div
          style={{
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: antdToken.colorPrimary,
            fontWeight: 700,
            fontSize: siderCollapsed ? 16 : 18,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          {siderCollapsed ? 'NV' : '网络验证系统'}
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ borderRight: 0 }}
        />
      </Sider>

      <Layout>
        <Header
          style={{
            background: antdToken.colorBgContainer,
            padding: '0 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button
              type="text"
              icon={siderCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={toggleSider}
              aria-label="切换侧边栏"
            />
            <Breadcrumb items={breadcrumbItems} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Button type="text" icon={<BellOutlined />} aria-label="通知" />
            <Dropdown
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
              placement="bottomRight"
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  padding: '0 4px',
                }}
              >
                <Avatar icon={<UserOutlined />} />
                <span>{user?.username ?? '未登录'}</span>
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content
          style={{
            margin: 16,
            padding: 24,
            background: antdToken.colorBgContainer,
            borderRadius: 8,
            minHeight: 280,
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
